// P3 — Calendrier 4 mois + durée + surcharges.

import { S } from './state.js';
import { $, $$ } from './dom.js';
import { MONTH_NAMES, DOW_SHORT, FANUC_MODELS } from './data.js';
import { isHoliday } from './holidays.js';
import { HOURS_PER_DAY } from './config.js';

// HOURS_PER_DAY est désormais importé depuis config.js (sourcé de la
// settings WP `hours_per_day`). Tous les calculs jours↔heures partagent
// cette valeur côté frontend.

// Jours fallback pour modèles unknown/noinfonow selon la taille estimée
const SIZE_DAYS = { small: 0.5, medium: 1, large: 1, xlarge: 1.5 };

// Fenêtre de 2 mois affichée (Direction A : deux calendriers côte à côte).
// On peut décaler jusqu'à 10 mois dans le futur — donc le dernier mois
// visible couvre mois+11 → horizon ~1 an.
const VISIBLE_MONTHS = 2;
const MAX_CAL_OFFSET = 10;

export function startOfDay( d ) {
	return new Date( d.getFullYear(), d.getMonth(), d.getDate() );
}

export function sameDate( a, b ) {
	if ( ! a || ! b ) return false;
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function fmtDateLong( d ) {
	var fr = S.lang === 'fr';
	var days = fr
		? [ 'dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi' ]
		: [ 'Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday' ];
	var dayName   = days[ d.getDay() ];
	var monthName = MONTH_NAMES[ S.lang ][ d.getMonth() ];
	if ( fr ) {
		return dayName + ' ' + d.getDate() + ' ' + monthName + ' ' + d.getFullYear();
	}
	return dayName + ', ' + monthName + ' ' + d.getDate() + ', ' + d.getFullYear();
}

/**
 * v1.1.16 — Calcule les jours de déplacement du devis courant (basé sur le
 * mode de transport + durée du drive). Mirror de la logique PHP dans
 * Soumission::get_booked_dates() pour cohérence.
 *
 *   fly                            → J-1 (arrivée) + J+1 (retour)
 *   drive > 90 min                 → J-1 (arrivée veille) ; J+1 si > 240 min
 *   drive ≤ 90 min  ou  pas d'info → aucun travel day
 *
 * Retourne un Set de clés YYYY-M-D (sans zero-padding, format calendrier).
 */
/**
 * v1.1.30 — Helper centralisé pour détecter le mode transport.
 * Certains code paths (airports.js applyTransportResult fly branch pre-1.1.30)
 * settent S.airport sans `mode` explicite — on infère via la présence de
 * `.c` (code IATA = fly) vs `.driveKm` (= drive).
 */
function getCurrentTransportMode() {
	if ( ! S.airport ) return '';
	if ( S.airport.mode === 'fly' || S.airport.mode === 'drive' ) {
		return S.airport.mode;
	}
	if ( S.airport.c ) return 'fly';
	if ( S.airport.driveKm > 0 || S.airport.driveMinutes > 0 ) return 'drive';
	return '';
}

export function computeCurrentTravelDays() {
	var out = new Set();
	if ( ! S.selDate ) return out;
	var mode     = getCurrentTransportMode();
	var driveMin = ( S.airport && S.airport.driveMinutes ) || 0;
	var needsBefore = false;
	var needsAfter  = false;
	if ( mode === 'fly' ) {
		needsBefore = true;
		needsAfter  = true;
	} else if ( mode === 'drive' && driveMin > 90 ) {
		needsBefore = true;
		needsAfter  = driveMin > 240;
	}
	if ( ! needsBefore && ! needsAfter ) return out;

	var endDate = S.endDate || S.selDate;
	if ( needsBefore ) {
		var before = new Date( S.selDate );
		before.setDate( before.getDate() - 1 );
		out.add( before.getFullYear() + '-' + ( before.getMonth() + 1 ) + '-' + before.getDate() );
	}
	if ( needsAfter ) {
		var after = new Date( endDate );
		after.setDate( after.getDate() + 1 );
		out.add( after.getFullYear() + '-' + ( after.getMonth() + 1 ) + '-' + after.getDate() );
	}
	return out;
}

export function buildMonthHTML( year, month, today ) {
	var first    = new Date( year, month, 1 );
	var daysIn   = new Date( year, month + 1, 0 ).getDate();
	var firstDow = first.getDay(); // 0=Sun..6=Sat
	var lead     = ( firstDow === 0 ) ? 6 : firstDow - 1; // lundi-first
	var monthLbl = MONTH_NAMES[ S.lang ][ month ] + ' ' + year;
	// v1.1.16 — précalcule les travel days du devis courant.
	var currentTravelDays = computeCurrentTravelDays();
	// Days within 14 days from today get the `urgent` class (+25% surcharge
	// auto-detected at pickDay). Visualised as a small red triangle so the
	// customer can see the cost implication before clicking.
	var URGENT_MS = 14 * 24 * 60 * 60 * 1000;
	// Holiday detection — country comes from the address Places resolved.
	// Falls back to 'CA' when unknown (most customers are domestic).
	var country = ( S.addressComponents && S.addressComponents.country_short ) || 'CA';

	var html = '<div class="qc-month" data-year="' + year + '" data-month="' + month + '">';
	html += '<div class="qc-head">' + monthLbl + '</div>';
	html += '<div class="qc-dow">';
	DOW_SHORT[ S.lang ].forEach( function ( l ) { html += '<span>' + l + '</span>'; } );
	html += '</div><div class="qc-grid">';

	for ( var i = 0; i < lead; i++ ) html += '<span class="qc-d empty"></span>';
	for ( var d = 1; d <= daysIn; d++ ) {
		var cur     = new Date( year, month, d );
		var dow     = cur.getDay();
		var past    = cur < today;
		var weekend = ( dow === 0 || dow === 6 );
		var isToday = sameDate( cur, today );
		var isSel   = sameDate( cur, S.selDate );
		var isEnd   = sameDate( cur, S.endDate );
		var inRange = S.selDate && S.endDate && cur > S.selDate && cur < S.endDate;
		var urgent      = ! past && ( cur.getTime() - today.getTime() ) < URGENT_MS;
		var holName     = isHoliday( cur, country );
		var dateKey     = year + '-' + ( month + 1 ) + '-' + d;
		var unavailable = ! past && S.unavailable && S.unavailable.has( dateKey );
		// v1.1.6 — travelDays : journées où un tech est en déplacement (J-1
		// ou J+1 d'une intervention déjà réservée). Style visuel distinct
		// (ambre) ET bloquées pour éviter d'envoyer un 2e tech sur la route
		// le même jour.
		var travelDay = ! past && ! unavailable && S.travelDays && S.travelDays.has( dateKey );
		// v1.1.26 — Travel days d'AUTRES indispos (soumissions WP existantes
		// + dates Google Calendar) sont maintenant hachurés comme unavailable
		// (pas distincts visuellement). Le tech ne peut pas être à 2 endroits
		// en même temps — peu importe que ce soit intervention ou travel.
		// On garde la flag pour le tooltip mais on applique la classe unavailable.
		if ( travelDay ) {
			unavailable = true;
		}
		// v1.1.16 — travel day du devis EN COURS de saisie (calculé selon mode
		// transport + drive_minutes). Visuel jaune (travel-current). Garde la
		// priorité visuelle même si la cellule serait conflictuelle si re-picked
		// — le client voit clairement ses jours de transport actuels.
		var currentTravelDay = ! past && ! unavailable && currentTravelDays.has( dateKey );

		// v1.1.26/v1.1.27/v1.1.28 — Conflict check : si la sélection de ce jour
		// comme intervention chevauche une date bloquée (intervention OU travel
		// d'un autre devis/Google), bloquer cette sélection. Skip si la cellule
		// est déjà un travel day de la sélection courante (déjà décidé).
		//
		// v1.1.28 — Important : on vérifie À LA FOIS S.unavailable ET S.travelDays.
		// Les J-1/J+1 des autres bookings sont dans S.travelDays, pas dans
		// S.unavailable. Sans ce check combiné, on ratait les conflits où
		// l'intervention multi-jours touchait un travel day externe.
		// v1.1.32 — Skip conflict check si la cellule est déjà PART de la
		// sélection courante (isSel, isEnd, inRange). C'est un déjà-décidé,
		// pas un "potentiel pick". Sinon le visuel conflict-block écrase le
		// bleu de sélection sur les jours intérieurs d'une intervention
		// multi-jours quand leur potentiel start créerait un conflit cascade.
		var hasTravelConflict = false;
		if ( ! past && ! isToday && ! unavailable && ! currentTravelDay
			&& ! isSel && ! isEnd && ! inRange
			&& ( ( S.unavailable && S.unavailable.size > 0 )
				|| ( S.travelDays && S.travelDays.size > 0 ) ) ) {
			var durDays  = Math.max( 1, Math.ceil( S.duration || 1 ) );
			var mode     = getCurrentTransportMode();
			var driveMin = ( S.airport && S.airport.driveMinutes ) || 0;
			var needsBefore = ( mode === 'fly' ) || ( mode === 'drive' && driveMin > 90 );
			var needsAfter  = ( mode === 'fly' ) || ( mode === 'drive' && driveMin > 240 );

			var isBlocked = function ( k ) {
				return ( S.unavailable && S.unavailable.has( k ) )
					|| ( S.travelDays  && S.travelDays.has(  k ) );
			};

			// Check chaque jour d'intervention (day 1 .. day durDays-1).
			// day 0 = la cellule elle-même, déjà testée via `unavailable`.
			for ( var di = 1; di < durDays && ! hasTravelConflict; di++ ) {
				var ivD = new Date( cur );
				ivD.setDate( ivD.getDate() + di );
				var ivKey = ivD.getFullYear() + '-' + ( ivD.getMonth() + 1 ) + '-' + ivD.getDate();
				if ( isBlocked( ivKey ) ) {
					hasTravelConflict = true;
				}
			}

			if ( ! hasTravelConflict && needsBefore ) {
				var before = new Date( cur );
				before.setDate( before.getDate() - 1 );
				var beforeKey = before.getFullYear() + '-' + ( before.getMonth() + 1 ) + '-' + before.getDate();
				if ( isBlocked( beforeKey ) ) hasTravelConflict = true;
			}
			if ( ! hasTravelConflict && needsAfter ) {
				var afterD = new Date( cur );
				afterD.setDate( afterD.getDate() + durDays );
				var afterKey = afterD.getFullYear() + '-' + ( afterD.getMonth() + 1 ) + '-' + afterD.getDate();
				if ( isBlocked( afterKey ) ) hasTravelConflict = true;
			}
			// v1.1.31 — hasTravelConflict NE set PLUS unavailable=true.
			// Visuel séparé : conflict = grisé non-cliquable mais pas hachuré
			// (distinct des vraies indispos). Permet au client de distinguer
			// "date prise" vs "date bloquée à cause d'une cascade".
		}

		var cls         = 'qc-d';
		if ( past )        cls += ' past';
		if ( weekend )     cls += ' weekend';
		if ( urgent )      cls += ' urgent';
		if ( holName )     cls += ' holiday';
		if ( unavailable ) cls += ' unavailable';
		if ( hasTravelConflict ) cls += ' conflict-block';
		if ( currentTravelDay ) cls += ' travel-day travel-current';
		if ( isToday )     cls += ' today';
		if ( isSel )       cls += ' sel range-start';
		else if ( isEnd )  cls += ' sel range-end';
		else if ( inRange ) cls += ' range';
		var titleParts = [];
		if ( holName ) titleParts.push( holName );
		if ( travelDay ) {
			titleParts.push( S.lang === 'fr' ? 'Journée de transport tech (autre devis)' : 'Tech travel day (other quote)' );
		} else if ( hasTravelConflict ) {
			titleParts.push( S.lang === 'fr' ? 'Sélection bloquée — travel en conflit avec une indispo' : 'Blocked — travel conflicts with existing busy day' );
		} else if ( unavailable ) {
			titleParts.push( S.lang === 'fr' ? 'Indisponible' : 'Unavailable' );
		}
		if ( currentTravelDay ) titleParts.push( S.lang === 'fr' ? 'Journée de transport de votre tech' : 'Your tech travel day' );
		var titleAttr = titleParts.length ? ' title="' + titleParts.join( ' · ' ).replace( /"/g, '&quot;' ) + '"' : '';
		// v1.1.15 — today aussi désactivé (impossible de booker une intervention le
		// jour même : il faut au minimum J+1 pour prep tech + transport).
		// v1.1.31 — hasTravelConflict aussi non-cliquable (mais visuel distinct).
		var attrs = ( past || isToday || unavailable || hasTravelConflict )
			? 'aria-disabled="true"'
			: 'data-ps-pickday="' + dateKey + '"';
		html += '<button type="button" class="' + cls + '"' + titleAttr + ' ' + attrs + '>' + d + '</button>';
	}
	html += '</div></div>';
	return html;
}

/**
 * v1.1.20 — Vérifie si une date YYYY-MM-DD est couverte par une fenêtre déjà
 * chargée depuis le serveur. Si non, on doit fetcher.
 */
function isDateLoaded( yyyyMmDd ) {
	if ( ! S.loadedRanges || ! S.loadedRanges.length ) return false;
	for ( var i = 0; i < S.loadedRanges.length; i++ ) {
		var r = S.loadedRanges[ i ];
		if ( yyyyMmDd >= r.from && yyyyMmDd <= r.to ) return true;
	}
	return false;
}

/**
 * v1.1.20 — S'assure que le mois visible (year, month) a ses booked dates
 * chargées. Si pas couvert, déclenche un fetch async vers /booked-dates.
 *
 * Le re-render arrive automatiquement quand le fetch résout (et merge dans
 * S.unavailable/travelDays). Léger flash possible avant que les dates
 * apparaissent grisées, acceptable.
 */
function ensureMonthLoaded( year, month ) {
	// On vérifie 3 points dans le mois (jour 1, 15, 28) pour être sûr que la
	// fenêtre couvre tout le mois.
	var probe1 = year + '-' + ( '0' + ( month + 1 ) ).slice( -2 ) + '-01';
	var probe2 = year + '-' + ( '0' + ( month + 1 ) ).slice( -2 ) + '-15';
	var probe3 = year + '-' + ( '0' + ( month + 1 ) ).slice( -2 ) + '-28';
	if ( isDateLoaded( probe1 ) && isDateLoaded( probe2 ) && isDateLoaded( probe3 ) ) {
		return;
	}
	// Pas couvert — fetch 89 jours depuis le 1er du mois.
	fetchBookedDates( probe1, 89 );
}

// Dédup : évite de relancer le même fetch tant qu'il est en vol.
var _fetchInflight = {};

function fetchBookedDates( fromDate, days ) {
	var key = fromDate + '|' + days;
	if ( _fetchInflight[ key ] ) return; // déjà en cours
	_fetchInflight[ key ] = true;

	var restUrl = ( window.ProbotSoum && window.ProbotSoum.restUrl )
		? window.ProbotSoum.restUrl
		: '/wp-json/probot-soum/v1/';
	var url = restUrl + 'booked-dates?from=' + encodeURIComponent( fromDate ) + '&days=' + days;

	fetch( url, { credentials: 'same-origin' } )
		.then( function ( r ) { return r.ok ? r.json() : null; } )
		.then( function ( json ) {
			delete _fetchInflight[ key ];
			if ( ! json || ! json.success || ! json.data ) return;
			var ranges_from = json.range && json.range.from ? json.range.from : fromDate;
			var ranges_days = json.range && json.range.days ? json.range.days : days;
			// Marquer la fenêtre comme chargée.
			var toTs = new Date( ranges_from + 'T00:00:00Z' ).getTime() + ranges_days * 86400000;
			var toIso = new Date( toTs ).toISOString().slice( 0, 10 );
			S.loadedRanges.push( { from: ranges_from, to: toIso } );
			// Merge dans les sets existants.
			var added = 0;
			( json.data.intervention || [] ).forEach( function ( k ) {
				if ( ! S.unavailable.has( k ) ) { S.unavailable.add( k ); added++; }
			} );
			( json.data.travel || [] ).forEach( function ( k ) {
				if ( ! S.travelDays.has( k ) && ! S.unavailable.has( k ) ) {
					S.travelDays.add( k );
					added++;
				}
			} );
			if ( window.console && window.console.log ) {
				console.log( '[ProbotSoum] Lazy load ' + fromDate + ' +' + days + 'd: +' + added + ' dates' );
			}
			if ( added > 0 ) renderQCal();
		} )
		.catch( function () { delete _fetchInflight[ key ]; } );
}

export function renderQCal() {
	var host = $( 'qcal' );
	if ( ! host ) return;
	var today  = startOfDay( new Date() );
	var off    = Math.max( 0, Math.min( MAX_CAL_OFFSET, S.calOffset || 0 ) );
	var baseY  = today.getFullYear();
	var baseM  = today.getMonth() + off;
	while ( baseM > 11 ) { baseM -= 12; baseY += 1; }
	var html = '';
	for ( var k = 0; k < VISIBLE_MONTHS; k++ ) {
		var ty = baseY;
		var tm = baseM + k;
		while ( tm > 11 ) { tm -= 12; ty += 1; }
		// v1.1.20 — Pre-fetch les booked dates si pas encore chargées.
		// Non bloquant : si fetch arrive après le render, on re-render via
		// renderQCal() à l'intérieur de fetchBookedDates.
		ensureMonthLoaded( ty, tm );
		html += buildMonthHTML( ty, tm, today );
	}
	host.innerHTML = html;
	updateCalNav();
}

export function updateCalNav() {
	var prev = $( 'qcalPrev' );
	var next = $( 'qcalNext' );
	var off  = Math.max( 0, Math.min( MAX_CAL_OFFSET, S.calOffset || 0 ) );
	if ( prev ) prev.disabled = ( off <= 0 );
	if ( next ) next.disabled = ( off >= MAX_CAL_OFFSET );
}

export function shiftCalendar( delta ) {
	var off = ( S.calOffset || 0 ) + delta;
	if ( off < 0 ) off = 0;
	if ( off > MAX_CAL_OFFSET ) off = MAX_CAL_OFFSET;
	if ( off === S.calOffset ) return;
	S.calOffset = off;
	renderQCal();
}

export function pickDay( yyyy, mm, dd ) {
	var today = startOfDay( new Date() );
	var d = new Date( yyyy, mm - 1, dd );
	// v1.1.15 — bloque aujourd'hui (J0) en plus du passé. Garde-fou défensif,
	// car le bouton est déjà aria-disabled dans buildMonthHTML.
	if ( d <= today ) return;
	// Reject any day flagged unavailable by tech-availability data.
	// v1.1.6 — Reject aussi les travel days (J-1/J+1 d'un autre devis).
	var key = yyyy + '-' + mm + '-' + dd;
	if ( S.unavailable && S.unavailable.has( key ) ) return;
	if ( S.travelDays  && S.travelDays.has(  key ) ) return;
	// v1.1.27/v1.1.28 — Reject si un des jours d'intervention (durDays multi-jours)
	// ou un des travel J-1/J+1 chevauche une indispo OU un travel day d'un
	// autre booking. Garde-fou en complément du blocage visuel.
	var isBlockedJ = function ( k ) {
		return ( S.unavailable && S.unavailable.has( k ) )
			|| ( S.travelDays  && S.travelDays.has(  k ) );
	};
	var durDays = Math.max( 1, Math.ceil( S.duration || 1 ) );
	for ( var di = 1; di < durDays; di++ ) {
		var ivD = new Date( d );
		ivD.setDate( ivD.getDate() + di );
		var ivKey = ivD.getFullYear() + '-' + ( ivD.getMonth() + 1 ) + '-' + ivD.getDate();
		if ( isBlockedJ( ivKey ) ) return;
	}
	var mode2     = getCurrentTransportMode();
	var driveMin2 = ( S.airport && S.airport.driveMinutes ) || 0;
	if ( mode2 === 'fly' || ( mode2 === 'drive' && driveMin2 > 90 ) ) {
		var jm1 = new Date( d );
		jm1.setDate( jm1.getDate() - 1 );
		var jm1Key = jm1.getFullYear() + '-' + ( jm1.getMonth() + 1 ) + '-' + jm1.getDate();
		if ( isBlockedJ( jm1Key ) ) return;
	}
	if ( mode2 === 'fly' || ( mode2 === 'drive' && driveMin2 > 240 ) ) {
		var jp1 = new Date( d );
		jp1.setDate( jp1.getDate() + durDays );
		var jp1Key = jp1.getFullYear() + '-' + ( jp1.getMonth() + 1 ) + '-' + jp1.getDate();
		if ( isBlockedJ( jp1Key ) ) return;
	}
	S.selDate = d;
	recomputeEndDate();

	var dow = d.getDay();
	S.surcharges.weekend = ( dow === 0 || dow === 6 );

	var MS_14D = 14 * 24 * 60 * 60 * 1000;
	S.surcharges.urgence = ( ( d.getTime() - today.getTime() ) < MS_14D );

	// Holiday flag — based on address country (CA fallback).
	var country = ( S.addressComponents && S.addressComponents.country_short ) || 'CA';
	S.surcharges.holiday = !! isHoliday( d, country );

	renderQCal();
	updateDateUI();
	updateSurchargeUI();
}

// Calcule endDate = selDate + ceil(duration) - 1 jours calendaires.
// Une durée de 0 ou 1 laisse endDate = selDate (un seul jour sélectionné).
export function recomputeEndDate() {
	if ( ! S.selDate ) { S.endDate = null; return; }
	var days = Math.max( 1, Math.ceil( S.duration || 1 ) );
	var end = new Date( S.selDate );
	end.setDate( end.getDate() + days - 1 );
	S.endDate = end;
}

export function updateDateUI() {
	var picked = $( 'datePicked' );
	var txt    = $( 'datePickedTxt' );
	var err    = $( 'dateErr' );
	// Submit-from-date button: maintenance flow exposes btnNx3 (4-step),
	// service flow exposes btnNx5 (6-step Direction A). Toggle whichever
	// is in the DOM.
	var nx     = $( 'btnNx5' ) || $( 'btnNx3' );
	if ( S.selDate ) {
		if ( txt ) {
			var fr = S.lang === 'fr';
			if ( S.endDate && ! sameDate( S.selDate, S.endDate ) ) {
				var days = Math.round( ( S.endDate - S.selDate ) / 86400000 ) + 1;
				txt.textContent = ( fr ? 'Du ' : 'From ' ) + fmtDateLong( S.selDate )
					+ ( fr ? ' au ' : ' to ' ) + fmtDateLong( S.endDate )
					+ '  ·  ' + days + ( fr ? ' jours' : ' days' );
			} else {
				txt.textContent = fmtDateLong( S.selDate );
			}
		}
		if ( picked ) picked.classList.add( 'on' );
		if ( err )    err.classList.remove( 'on' );
		if ( nx )     nx.disabled = false;
		// v1.1.6 — Warning si J-1 (arrivée) ou J+1 (retour) tombe sur un férié.
		// Ex. fin de mission le 24 déc → vol retour 25 déc (Noël) = pas tenable.
		checkTravelDayHolidays();
	} else {
		if ( picked ) picked.classList.remove( 'on' );
		if ( nx )     nx.disabled = true;
		clearTravelDayWarning();
	}
}

/**
 * v1.1.33 — Refactor du warning : maintenant détecte weekend + jour férié +
 * urgence (< 14j), sur les jours d'intervention ET les travel days (J-1/J+1).
 * Ton informatif "votre date implique un surcoût" plutôt qu'alarme bloquante.
 */
function checkTravelDayHolidays() {
	if ( ! S.selDate || ! S.endDate ) { clearTravelDayWarning(); return; }
	var country = ( S.addressComponents && S.addressComponents.country ) || 'CA';
	var fr = S.lang === 'fr';
	var todayTs   = startOfDay( new Date() ).getTime();
	var URGENT_MS = 14 * 86400000;

	function triggersFor( d ) {
		var t = [];
		if ( d.getDay() === 0 || d.getDay() === 6 ) {
			t.push( fr ? 'weekend' : 'weekend' );
		}
		var h = isHoliday( d, country );
		if ( h ) {
			t.push( ( fr ? 'férié — ' : 'holiday — ' ) + h );
		}
		if ( ( d.getTime() - todayTs ) < URGENT_MS ) {
			t.push( fr ? 'urgence (< 14 jours)' : 'urgent (< 14 days)' );
		}
		return t;
	}

	var lines = [];

	// Intervention days
	var cur = new Date( S.selDate );
	var safety = 0;
	while ( cur.getTime() <= S.endDate.getTime() && safety < 60 ) {
		var trig = triggersFor( cur );
		if ( trig.length ) {
			lines.push( ( fr ? 'Intervention ' : 'Intervention ' ) + fmtDateLong( cur ) + ' — ' + trig.join( ', ' ) );
		}
		cur = new Date( cur.getTime() + 86400000 );
		safety++;
	}

	// Travel days
	var mode     = getCurrentTransportMode();
	var driveMin = ( S.airport && S.airport.driveMinutes ) || 0;
	var needsBefore = ( mode === 'fly' ) || ( mode === 'drive' && driveMin > 90 );
	var needsAfter  = ( mode === 'fly' ) || ( mode === 'drive' && driveMin > 240 );
	if ( needsBefore ) {
		var jm1 = new Date( S.selDate.getTime() - 86400000 );
		var t1  = triggersFor( jm1 );
		if ( t1.length ) {
			lines.push( ( fr ? 'Arrivée tech J-1 ' : 'Tech arrival J-1 ' ) + fmtDateLong( jm1 ) + ' — ' + t1.join( ', ' ) );
		}
	}
	if ( needsAfter ) {
		var jp1 = new Date( S.endDate.getTime() + 86400000 );
		var t2  = triggersFor( jp1 );
		if ( t2.length ) {
			lines.push( ( fr ? 'Retour tech J+1 ' : 'Tech return J+1 ' ) + fmtDateLong( jp1 ) + ' — ' + t2.join( ', ' ) );
		}
	}

	var warnEl = document.getElementById( 'travelHolidayWarn' );
	if ( lines.length === 0 ) {
		if ( warnEl ) warnEl.style.display = 'none';
		return;
	}
	if ( ! warnEl ) {
		warnEl = document.createElement( 'div' );
		warnEl.id = 'travelHolidayWarn';
		warnEl.style.cssText = 'margin-top:10px;padding:12px 16px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;font-size:13px;color:#78350F;line-height:1.5';
		var picked = document.getElementById( 'datePicked' );
		if ( picked && picked.parentNode ) picked.parentNode.insertBefore( warnEl, picked.nextSibling );
	}
	warnEl.style.display = 'block';
	var title = fr
		? 'Frais additionnels possibles selon votre date'
		: 'Possible surcharges based on your date';
	var hint  = fr
		? 'Ces conditions s\'ajoutent au tarif standard (weekend, jour férié, ou délai < 14 jours).'
		: 'These conditions add to standard pricing (weekend, holiday, or rush < 14 days).';
	warnEl.innerHTML = '💰 <strong>' + title + '</strong>'
		+ '<ul style="margin:6px 0 6px 18px;padding:0">'
		+ lines.map( function ( l ) { return '<li>' + l + '</li>'; } ).join( '' )
		+ '</ul>'
		+ '<span style="font-size:12px;opacity:.85">' + hint + '</span>';
}

function clearTravelDayWarning() {
	var warnEl = document.getElementById( 'travelHolidayWarn' );
	if ( warnEl ) warnEl.style.display = 'none';
}

// Dérive la durée (en jours) des robots sélectionnés + du catalogue.
// Appelée par renderSelModels() quand la sélection change.
// v1.1.6 — Ajout +1h buffer arrivée 1ère journée (setup outils, briefing,
// déchargement). Le safety/SST training est compris dans la journée
// complète facturée (pas d'heures additionnelles).
export function computeDuration() {
	var totalHours = 0;
	Object.keys( S.selModels ).forEach( function ( id ) {
		var qty = S.selModels[ id ] || 0;
		if ( ! qty ) return;
		var hoursPerUnit = HOURS_PER_DAY; // default 1 jour si inconnu
		if ( id === 'unknown' || id === 'noinfonow' ) {
			var sz = S.sizeByRow[ id ];
			hoursPerUnit = sz && SIZE_DAYS[ sz ] != null ? SIZE_DAYS[ sz ] * HOURS_PER_DAY : HOURS_PER_DAY;
		} else {
			var m = FANUC_MODELS.find( function ( x ) { return x.id === id; } );
			if ( m && typeof m.hours === 'number' ) {
				hoursPerUnit = m.hours;
			}
		}
		totalHours += hoursPerUnit * qty;
	} );
	// v1.1.6 — Buffer +1h pour arrivée/setup la 1ère journée (déballage outils,
	// briefing avec contact site, sécurité de base). Ajoute juste 1h, pas une
	// journée complète — le tech reste productif le jour 1.
	if ( totalHours > 0 ) {
		totalHours += 1;
	}
	var total = totalHours / HOURS_PER_DAY;
	// v1.1.6 — Arrondi au JOUR supérieur (pas .5). Le tech est sur place une
	// journée complète ou pas — facturer .5 journée n'a aucun sens quand on
	// fly un tech pour intervenir. Ex. 1 robot 10h + 1h arrivée = 11h →
	// 2 jours, pas 1.5. 8h + 1h = 9h → 1 jour (acceptable de pousser 1h).
	S.duration = Math.max( 1, Math.ceil( total ) );
	// Si une date de départ est déjà choisie, maintenir la plage en phase.
	if ( S.selDate ) {
		recomputeEndDate();
		if ( $( 'qcal' ) ) renderQCal();
		updateDateUI();
	}
	return S.duration;
}

export function toggleSurcharge( key ) {
	if ( ! ( key in S.surcharges ) ) return;
	S.surcharges[ key ] = ! S.surcharges[ key ];
	updateSurchargeUI();
}

export function updateSurchargeUI() {
	$$( '.probot-soum [data-ps-sur]' ).forEach( function ( b ) {
		var k = b.getAttribute( 'data-ps-sur' );
		var on = !! S.surcharges[ k ];
		b.classList.toggle( 'on', on );
		if ( b.hasAttribute( 'aria-pressed' ) ) {
			b.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
		}
	} );
}
